import React, { useRef, useState, useCallback, useLayoutEffect } from "react";
import { MODES, ESC_ACCENT } from "../../constants/modes.js";
import {
  TONE_OPTIONS,
  POST_LENGTH_OPTIONS,
  CTA_TYPE_OPTIONS,
  CAROUSEL_SLIDE_OPTIONS,
  BLOG_LENGTH_OPTIONS,
  SEO_INTENT_OPTIONS,
  EMAIL_LENGTH_OPTIONS,
  ADS_CHANNEL_OPTIONS,
  CASE_STUDY_SECTION_OPTIONS,
  CASE_STUDY_FOCUS_OPTIONS,
  LANDING_HEADLINE_OPTIONS,
} from "../../constants/options.js";
import { SOCIAL_CHANNELS, EMAIL_CHANNELS } from "../../constants/channels.js";
import { normalizeUrl } from "../../utils/textProcessing.js";
import {
  Zap,
  PenLine,
  Paperclip,
  Target,
  Calendar,
  Layers,
  Search,
  BookOpen,
  Upload,
  X,
  FileText,
  HelpCircle,
  Download,
} from "lucide-react";
import {
  getBundle,
  uploadFileToBundle,
  deleteBundleFile,
} from "../../services/api/uploadClient.js";
import {
  importParamsFromFile,
  ImportParamsError,
} from "../../utils/importParamsFromFile.js";

// ── Tooltip component ───────────────────────────────────────────────────────
const Tooltip = ({ text, color }) => {
  const [visible, setVisible] = useState(false);
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        cursor: "help",
      }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <HelpCircle
        size={13}
        style={{
          color: color || "#888",
          opacity: 0.6,
          transition: "opacity 0.15s",
        }}
      />
      {visible && (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1a1a2e",
            color: "#e4e4ec",
            fontSize: "12px",
            lineHeight: 1.5,
            padding: "10px 14px",
            borderRadius: "10px",
            whiteSpace: "normal",
            width: "260px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.1)",
            zIndex: 1000,
            pointerEvents: "none",
            fontWeight: 400,
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: "normal",
            textTransform: "none",
          }}
        >
          {text}
          <span
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #1a1a2e",
            }}
          />
        </span>
      )}
    </span>
  );
};

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".pptx", ".xlsx", ".txt", ".md"];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

const AutoGrowTextarea = ({ value, onChange, placeholder, style }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = "auto";
    ref.current.style.height = ref.current.scrollHeight + "px";
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={1}
      style={style}
    />
  );
};

const Step1Setup = ({
  state,
  dispatch,
  onSuggest,
  color,
  w,
  t,
  styles = {},
  personaOptions = [],
  objectiveOptions = [],
}) => {
  const {
    activeMode,
    focusTopic,
    taskText,
    taskContract,
    webSearch,
    kbSearch,
    persona,
    tone,
    customTone,
    objective,
    ctaUrl,
    ctaText,
    referenceUrls,
    refsEnabled,
    userKbEnabled,
    userKbQuery,
    linkedInParams,
    carouselParams,
    blogParams,
    emailParams,
    adsParams,
    caseStudyParams,
    landingParams,
    contentFormat,
    socialChannel,
    emailChannel,
    uploadBundle,
  } = state;
  const c = color || ESC_ACCENT;

  const set = (key, val) => dispatch({ type: "SET_KEY", key, value: val });

  // ─── Blog-SEO keywords mutators ─────────────────────────────────────────
  const setKeywords = (newKws) =>
    set("blogParams", { ...blogParams, keywords: newKws });
  const setPrimaryKeyword = (val) =>
    setKeywords({ ...blogParams.keywords, primary: val });
  const setSecondarySlot = (idx, val) => {
    const next = [...blogParams.keywords.secondary];
    while (next.length <= idx)
      next.push({ keyword: "", monthly_volume: null });
    next[idx] = { ...next[idx], keyword: val };
    setKeywords({ ...blogParams.keywords, secondary: next });
  };

  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);

  const [dragOver, setDragOver] = useState(false);
  const [importStatus, setImportStatus] = useState(null); // { type: 'ok' | 'err', msg }

  const handleImportSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setImportStatus(null);
      try {
        const formState = await importParamsFromFile(file);
        dispatch({ type: "SET_BATCH", payload: formState });
        setImportStatus({
          type: "ok",
          msg: `Parametri importati da "${file.name}"`,
        });
      } catch (err) {
        const msg =
          err instanceof ImportParamsError
            ? err.message
            : `Errore importazione: ${err.message || err}`;
        setImportStatus({ type: "err", msg });
      }
    },
    [dispatch],
  );

  const processFile = useCallback(
    async (file) => {
      if (!file) return;
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        dispatch({
          type: "SET_UPLOAD_BUNDLE_ERROR",
          error: `Tipo di file non supportato (${ext}). Formati ammessi: ${ALLOWED_EXTENSIONS.join(", ")}`,
        });
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        dispatch({
          type: "SET_UPLOAD_BUNDLE_ERROR",
          error: `File troppo grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Limite: 50 MB`,
        });
        return;
      }
      dispatch({ type: "SET_UPLOAD_BUNDLE_LOADING", value: true });
      try {
        await uploadFileToBundle(file);
        const bundle = await getBundle();
        dispatch({
          type: "SET_UPLOAD_BUNDLE_FILES",
          files: bundle.files || [],
        });
      } catch (err) {
        dispatch({ type: "SET_UPLOAD_BUNDLE_ERROR", error: err.message });
      }
    },
    [dispatch],
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    processFile(file);
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      processFile(file);
    },
    [processFile],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleDeleteFile = async (filename) => {
    dispatch({ type: "SET_UPLOAD_BUNDLE_LOADING", value: true });
    try {
      await deleteBundleFile(filename);
      const bundle = await getBundle();
      dispatch({ type: "SET_UPLOAD_BUNDLE_FILES", files: bundle.files || [] });
    } catch (err) {
      dispatch({ type: "SET_UPLOAD_BUNDLE_ERROR", error: err.message });
    }
  };

  const inp = {
    width: "100%",
    padding: "12px 14px",
    background: t.inputBg,
    border: `1px solid ${t.inputBorder}`,
    borderRadius: "10px",
    color: t.text,
    fontSize: "14px",
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    boxSizing: "border-box",
  };
  const lbl = {
    fontSize: "10px",
    fontFamily: "'JetBrains Mono', monospace",
    color: t.textFaint,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };
  const sec = {
    background: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: "14px",
    padding: "20px",
    marginBottom: "12px",
  };

  const HelperLine = ({ children }) => (
    <div
      style={{
        fontSize: "12px",
        color: t.textMuted,
        fontStyle: "italic",
        marginTop: 0,
        marginBottom: "12px",
        lineHeight: 1.5,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {children}
    </div>
  );

  const Sel = ({ label, helper, value, onChange, options }) => (
    <div>
      <label style={lbl}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inp, appearance: "none", cursor: "pointer" }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {helper && (
        <div
          style={{
            fontSize: "12px",
            color: t.textMuted,
            fontStyle: "italic",
            marginTop: "6px",
            lineHeight: 1.5,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {helper}
        </div>
      )}
    </div>
  );

  const activeModeName =
    MODES.find((m) => m.id === activeMode)?.name || "Contenuto";
  const paramsCardTitle = `PARAMETRI ${activeModeName.toUpperCase()}`;

  return (
    <div>
      {/* 5: Platform Selection */}
      <div style={sec}>
        <div style={lbl}>
          TIPO DI CONTENUTO{" "}
          <Tooltip
            text="Seleziona il tipo di contenuto da generare. Ogni modalità ha parametri specifici e produce output ottimizzato per la piattaforma scelta."
            color={c}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "8px",
          }}
        >
          {MODES.map((mode) => (
            <button
              key={mode.id}
              disabled={mode.disabled}
              onClick={
                mode.disabled
                  ? undefined
                  : () => {
                      set("activeMode", mode.id);
                      set("contentFormat", "post");
                      set("emailChannel", "email-classic");
                    }
              }
              title={
                mode.disabled
                  ? "Modalità in fase di implementazione"
                  : undefined
              }
              style={{
                position: "relative",
                padding: "10px 12px",
                borderRadius: "10px",
                border:
                  activeMode === mode.id
                    ? `2px solid ${mode.color}`
                    : `1px solid ${t.border}`,
                background:
                  activeMode === mode.id ? `${mode.color}15` : t.surface,
                color: activeMode === mode.id ? mode.color : t.textMuted,
                fontSize: "12px",
                fontWeight: activeMode === mode.id ? 700 : 400,
                cursor: mode.disabled ? "not-allowed" : "pointer",
                textAlign: "center",
                transition: "all 0.2s",
                opacity: mode.disabled ? 0.5 : 1,
              }}
            >
              {mode.name}
              {mode.disabled && (
                <span
                  style={{
                    display: "block",
                    marginTop: "4px",
                    fontSize: "9px",
                    fontFamily: "'JetBrains Mono', monospace",
                    color: t.textFaint,
                    letterSpacing: "0.8px",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  In arrivo
                </span>
              )}
            </button>
          ))}
        </div>
        {/* LinkedIn format toggle */}
        {activeMode === "linkedin-post" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginTop: "12px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ ...lbl, marginBottom: 0 }}>Formato:</span>
            <div
              style={{ display: "flex", gap: "8px", flexWrap: "wrap", flex: 1 }}
            >
              {[
                { v: "post", l: "Post" },
                { v: "carousel", l: "Carousel" },
              ].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => set("contentFormat", v)}
                  style={{
                    flex: "1 1 160px",
                    maxWidth: "260px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    border:
                      contentFormat === v
                        ? `2px solid #0A66C2`
                        : `1px solid ${t.border}`,
                    background:
                      contentFormat === v ? "rgba(10,102,194,0.1)" : t.surface,
                    color: contentFormat === v ? "#0A66C2" : t.textMuted,
                    fontSize: "12px",
                    fontWeight: contentFormat === v ? 700 : 400,
                    cursor: "pointer",
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Social channel selector */}
        {activeMode === "social" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginTop: "12px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ ...lbl, marginBottom: 0 }}>Canale:</span>
            <div
              style={{ display: "flex", gap: "8px", flexWrap: "wrap", flex: 1 }}
            >
              {SOCIAL_CHANNELS.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => set("socialChannel", ch.id)}
                  style={{
                    flex: "1 1 100px",
                    maxWidth: "130px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    border:
                      socialChannel === ch.id
                        ? `2px solid ${ch.color}`
                        : `1px solid ${t.border}`,
                    background:
                      socialChannel === ch.id ? `${ch.color}12` : t.surface,
                    color: socialChannel === ch.id ? ch.color : t.textMuted,
                    fontSize: "12px",
                    fontWeight: socialChannel === ch.id ? 700 : 400,
                    cursor: "pointer",
                  }}
                >
                  {ch.name}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Email channel selector */}
        {activeMode === "email" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginTop: "12px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ ...lbl, marginBottom: 0 }}>Canale:</span>
            <div
              style={{ display: "flex", gap: "8px", flexWrap: "wrap", flex: 1 }}
            >
              {EMAIL_CHANNELS.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => set("emailChannel", ch.id)}
                  style={{
                    flex: "1 1 160px",
                    maxWidth: "260px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    border:
                      emailChannel === ch.id
                        ? `2px solid ${ch.color}`
                        : `1px solid ${t.border}`,
                    background:
                      emailChannel === ch.id ? `${ch.color}12` : t.surface,
                    color: emailChannel === ch.id ? ch.color : t.textMuted,
                    fontSize: "12px",
                    fontWeight: emailChannel === ch.id ? 700 : 400,
                    cursor: "pointer",
                  }}
                >
                  {ch.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 1: Argomento / Tema / Soggetto */}
      <div style={sec}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            marginBottom: "6px",
          }}
        >
          <div style={{ ...lbl, marginBottom: 0 }}>
            <Zap size={13} /> DI COSA VUOI PARLARE *{" "}
            <Tooltip
              text="Il soggetto del contenuto in una breve frase. Non un'istruzione: scrivi il tema, non il verbo. Il sistema rileva automaticamente date, intento e tipo di contenuto."
              color={c}
            />
          </div>
          <button
            type="button"
            onClick={() => importInputRef.current?.click()}
            title="Importa parametri da un file .md / .docx / .json esportato da una generazione precedente"
            style={{
              display: "none",
              alignItems: "center",
              gap: "6px",
              padding: "6px 10px",
              fontSize: "11px",
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: "1px",
              textTransform: "uppercase",
              color: c,
              background: "transparent",
              border: `1px solid ${c}55`,
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            <Download size={12} /> Importa parametri
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".md,.txt,.doc,.docx,.html,.htm,.json"
            style={{ display: "none" }}
            onChange={handleImportSelect}
          />
        </div>
        {importStatus && (
          <div
            style={{
              fontSize: "12px",
              padding: "8px 10px",
              borderRadius: "8px",
              marginBottom: "10px",
              background:
                importStatus.type === "ok"
                  ? "rgba(16,185,129,0.1)"
                  : "rgba(239,68,68,0.1)",
              color:
                importStatus.type === "ok" ? "#10B981" : "#EF4444",
              border: `1px solid ${
                importStatus.type === "ok"
                  ? "rgba(16,185,129,0.3)"
                  : "rgba(239,68,68,0.3)"
              }`,
            }}
          >
            {importStatus.msg}
          </div>
        )}
        <input
          type="text"
          value={taskText}
          onChange={(e) =>
            dispatch({ type: "SET_TASK", value: e.target.value })
          }
          placeholder="Inserisci un solo argomento alla volta es. lead generation nel marketing b2b"
          style={inp}
        />
        {taskContract &&
          (taskContract.intent ||
            taskContract.contentType ||
            taskContract.dates.length > 0) && (
            <div
              style={{
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                marginTop: "8px",
              }}
            >
              {taskContract.intent && (
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "100px",
                    background: `${c}15`,
                    color: c,
                    fontSize: "11px",
                    fontWeight: 600,
                    border: `1px solid ${c}30`,
                  }}
                >
                  <Target
                    size={10}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: "3px",
                    }}
                  />
                  {taskContract.intent}
                </span>
              )}
              {taskContract.contentType && (
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "100px",
                    background: "rgba(139,92,246,0.1)",
                    color: "#8B5CF6",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid rgba(139,92,246,0.2)",
                  }}
                >
                  <Layers
                    size={10}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: "3px",
                    }}
                  />
                  {taskContract.contentType}
                </span>
              )}
              {taskContract.dates.map((d, i) => (
                <span
                  key={i}
                  style={{
                    padding: "3px 10px",
                    borderRadius: "100px",
                    background: "rgba(16,185,129,0.1)",
                    color: "#10B981",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid rgba(16,185,129,0.2)",
                  }}
                >
                  <Calendar
                    size={10}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: "3px",
                    }}
                  />
                  {d.normalized}
                </span>
              ))}
            </div>
          )}

        <div style={{ ...lbl, marginTop: "18px" }}>
          <PenLine size={13} /> QUALI PUNTI CHIAVE VUOI INSERIRE (FACOLTATIVO){" "}
          <Tooltip
            text="Punti chiave che il contenuto DEVE includere. Uno per riga. Questi vengono inseriti obbligatoriamente nella generazione e verificati dal fact-checker."
            color={c}
          />
        </div>
        <textarea
          value={state.keyPoints || ""}
          onChange={(e) => set("keyPoints", e.target.value)}
          placeholder="- Differenza tra lead generation B2B e B2C
- Canali più efficaci oggi Linkedin, Youtube, Email
- Allineamento tra team marketing e sales"
          style={{
            ...inp,
            resize: "vertical",
            minHeight: "90px",
            padding: "12px 14px",
          }}
        />

        <div style={{ ...lbl, marginTop: "14px" }}>
          <PenLine size={13} /> ANGOLO E INDICAZIONI EDITORIALI (FACOLTATIVO){" "}
          <Tooltip
            text="Framing, angolo, taglio editoriale, oppure fatti su di te o sulla tua azienda da assumere come veri. NON viene fact-checkato dal sistema: ciò che scrivi qui passa al contenuto come verità."
            color={c}
          />
        </div>
        <textarea
          value={focusTopic}
          onChange={(e) => set("focusTopic", e.target.value)}
          placeholder="- Sottolinea il vantaggio competitivo di Esc sul marketing B2B e valori differenzianti
- Focalizza il messaggio su risultati concreti ed evita confronti con concorrenti
- Utiliza alcune metafore o similitudini legate al mondo della vela"
          style={{
            ...inp,
            resize: "vertical",
            minHeight: "90px",
            padding: "12px 14px",
          }}
        />
      </div>

      {/* 6b: Parametri della modalità — sempre visibile (contiene Persona/Obiettivo/Tono).
          La sezione dei parametri specifici + i campi CTA sono nascosti per social. */}
      <div style={sec}>
        <div style={lbl}>
          {paramsCardTitle}{" "}
          <Tooltip
            text="Persona, obiettivo, tono e impostazioni specifiche per la modalità selezionata: lunghezza, formato, parametri SEO, ecc. Questi influenzano la struttura e il livello di dettaglio dell'output."
            color={c}
          />
        </div>

        {/* Top params row — landing: 3 cols × 2 rows [Persona, Obiettivo, Tone] / [Formula Headline, Social Proof];
            altri mode: 3 cols [Persona, Obiettivo, Tone]. */}
        {(() => {
          const personaField = (
            <Sel
              key="persona"
              label={
                <>
                  PERSONA{" "}
                  <Tooltip
                    text="Il ruolo o profilo del destinatario del contenuto. Influenza stile, competenza e linguaggio usato nella generazione."
                    color={c}
                  />
                </>
              }
              helper="A chi è indirizzato il contenuto da generare"
              value={persona}
              onChange={(v) => set("persona", v)}
              options={personaOptions}
            />
          );
          const objectiveField = (
            <Sel
              key="objective"
              label={
                <>
                  OBIETTIVO{" "}
                  <Tooltip
                    text="L'obiettivo di marketing del contenuto: engagement, lead generation, brand awareness, ecc. Guida il tono persuasivo e la call to action."
                    color={c}
                  />
                </>
              }
              helper="Il goal di marketing"
              value={objective}
              onChange={(v) => set("objective", v)}
              options={objectiveOptions}
            />
          );
          const toneField = (
            <div key="tone" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Sel
                label={
                  <>
                    TONE OF VOICE{" "}
                    <Tooltip
                      text="Lo stile comunicativo (subordinato alle Brand Guidelines Aziendali)"
                      color={c}
                    />
                  </>
                }
                helper={tone === "custom" ? null : "Lo stile comunicativo. Scegli 'Custom' per un tono libero."}
                value={tone}
                onChange={(v) => set("tone", v)}
                options={TONE_OPTIONS.map(o =>
                  o.value === "custom" && customTone?.trim()
                    ? { ...o, label: `Personalizzato — ${customTone.trim().length > 60 ? customTone.trim().slice(0, 59) + "…" : customTone.trim()}` }
                    : o
                )}
              />
              {tone === "custom" && (
                <>
                  <AutoGrowTextarea
                    value={customTone}
                    onChange={(e) => set("customTone", e.target.value)}
                    placeholder="Nome del tono personalizzato…"
                    style={{ ...inp, padding: "10px 14px", resize: "none", overflow: "hidden" }}
                  />
                  <div
                    style={{
                      fontSize: "12px",
                      color: t.textMuted,
                      fontStyle: "italic",
                      lineHeight: 1.5,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    Lo stile comunicativo. Scegli 'Custom' per un tono libero.
                  </div>
                </>
              )}
            </div>
          );
          const formulaHeadlineField = (
            <Sel
              key="formula-headline"
              label="FORMULA HEADLINE"
              value={landingParams.headlineFormula}
              onChange={(v) =>
                set("landingParams", {
                  ...landingParams,
                  headlineFormula: v,
                })
              }
              options={LANDING_HEADLINE_OPTIONS}
            />
          );
          const socialProofField = (
            <div key="social-proof" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ ...lbl, marginBottom: 0 }}>
                SOCIAL PROOF{" "}
                <Tooltip
                  text="Numeri, metriche o riconoscimenti che rafforzano la credibilità della landing. Verranno usati come bullets nella sezione SOCIAL PROOF."
                  color={c}
                />
              </label>
              <input
                type="text"
                value={landingParams.socialProof}
                onChange={(e) =>
                  set("landingParams", {
                    ...landingParams,
                    socialProof: e.target.value,
                  })
                }
                placeholder="es. 150+ PMI servite, +50% efficienza media"
                style={inp}
              />
            </div>
          );
          const isLanding = activeMode === "landing";
          const fields = isLanding
            ? [personaField, objectiveField, toneField, formulaHeadlineField, socialProofField]
            : [personaField, objectiveField, toneField];
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "14px",
                marginBottom: "16px",
              }}
            >
              {fields}
            </div>
          );
        })()}

        {/* Parametri specifici della modalità + CTA (nascosti per social) */}
        {!["social"].includes(activeMode) && (
          <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "12px",
            }}
          >
            {(activeMode === "linkedin-post" || contentFormat === "post") &&
              activeMode === "linkedin-post" && (
                <>
                  <Sel
                    label="Lunghezza Post"
                    value={linkedInParams.postLength}
                    onChange={(v) =>
                      set("linkedInParams", {
                        ...linkedInParams,
                        postLength: v,
                      })
                    }
                    options={POST_LENGTH_OPTIONS}
                  />
                  <Sel
                    label="Tipo CTA"
                    value={linkedInParams.ctaType}
                    onChange={(v) =>
                      set("linkedInParams", { ...linkedInParams, ctaType: v })
                    }
                    options={CTA_TYPE_OPTIONS}
                  />
                </>
              )}
            {activeMode === "linkedin-post" && contentFormat === "carousel" && (
              <Sel
                label="Numero Slide"
                value={carouselParams.slideCount}
                onChange={(v) =>
                  set("carouselParams", { ...carouselParams, slideCount: v })
                }
                options={CAROUSEL_SLIDE_OPTIONS}
              />
            )}
            {activeMode === "blog-seo" && (
              <>
                <Sel
                  label="Lunghezza Articolo"
                  value={blogParams.articleLength}
                  onChange={(v) =>
                    set("blogParams", { ...blogParams, articleLength: v })
                  }
                  options={BLOG_LENGTH_OPTIONS}
                />
                <Sel
                  label="SEO Intent"
                  value={blogParams.seoIntent}
                  onChange={(v) =>
                    set("blogParams", { ...blogParams, seoIntent: v })
                  }
                  options={SEO_INTENT_OPTIONS}
                />
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ ...lbl, marginBottom: "8px" }}>
                    Keywords (Facoltativo)
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                      alignItems: "start",
                    }}
                  >
                    <div>
                      <label style={{ ...lbl, marginBottom: "4px" }}>
                        Principale
                      </label>
                      <input
                        value={blogParams.keywords.primary}
                        onChange={(e) => setPrimaryKeyword(e.target.value)}
                        placeholder="es. time to market"
                        style={inp}
                      />
                    </div>
                    <div>
                      <label style={{ ...lbl, marginBottom: "4px" }}>
                        Secondarie
                      </label>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {[0, 1, 2].map((idx) => (
                          <input
                            key={idx}
                            value={
                              blogParams.keywords.secondary[idx]?.keyword || ""
                            }
                            onChange={(e) =>
                              setSecondarySlot(idx, e.target.value)
                            }
                            placeholder="es. go-to-market"
                            style={inp}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {activeMode === "email" && (
              <>
                <Sel
                  label="Lunghezza"
                  value={emailParams.emailLength}
                  onChange={(v) =>
                    set("emailParams", { ...emailParams, emailLength: v })
                  }
                  options={EMAIL_LENGTH_OPTIONS}
                />
                {emailChannel === "linkedin-newsletter" && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <label>
                        <input
                          type="checkbox"
                          checked={emailParams.includeCompanionPost}
                          onChange={(e) =>
                            set("emailParams", {
                              ...emailParams,
                              includeCompanionPost: e.target.checked,
                            })
                          }
                          style={{ marginRight: "6px" }}
                        />
                        Genera Companion Post
                      </label>
                    </div>
                  </>
                )}
              </>
            )}
            {activeMode === "ads" && (
              <>
                <Sel
                  label="Canale"
                  value={adsParams.channel}
                  onChange={(v) =>
                    set("adsParams", { ...adsParams, channel: v })
                  }
                  options={ADS_CHANNEL_OPTIONS}
                />
                <Sel
                  label="N. Gruppi Annunci"
                  value={adsParams.adGroups}
                  onChange={(v) =>
                    set("adsParams", { ...adsParams, adGroups: v })
                  }
                  options={[
                    { value: "1", label: "1 gruppo" },
                    { value: "2", label: "2 gruppi" },
                    { value: "3", label: "3 gruppi" },
                  ]}
                />
              </>
            )}
            {activeMode === "case-study" && (
              <>
                <Sel
                  label="Stile"
                  value={caseStudyParams.outputStyle}
                  onChange={(v) =>
                    set("caseStudyParams", {
                      ...caseStudyParams,
                      outputStyle: v,
                    })
                  }
                  options={CASE_STUDY_SECTION_OPTIONS}
                />
                <Sel
                  label="Focus Area"
                  value={caseStudyParams.focusArea}
                  onChange={(v) =>
                    set("caseStudyParams", { ...caseStudyParams, focusArea: v })
                  }
                  options={CASE_STUDY_FOCUS_OPTIONS}
                />
              </>
            )}
          </div>
          <input
            type="text"
            value={ctaText}
            onChange={(e) => set("ctaText", e.target.value)}
            placeholder="CTA testo (opzionale): es. Scopri di più, Prenota una demo..."
            style={{ ...inp, marginTop: "12px" }}
          />
          <input
            type="url"
            value={ctaUrl}
            onChange={(e) => set("ctaUrl", e.target.value)}
            onBlur={(e) => {
              const n = normalizeUrl(e.target.value);
              if (n !== e.target.value) set("ctaUrl", n);
            }}
            placeholder="URL CTA (opzionale): https://..."
            style={{ ...inp, marginTop: "12px" }}
          />
          </>
        )}
      </div>

      {/* 2: Ricerca e Fonti */}
      <div style={sec}>
        <div style={{ marginBottom: "10px" }}>
          <div style={lbl}>
            <Search size={13} /> VUOI INCLUDERE FONTI PROPRIETARIE E ONLINE?{" "}
            <Tooltip
              text="Attiva le fonti che il sistema userà per generare contenuti più accurati e coerenti con il tuo brand. Puoi combinare più fonti contemporaneamente."
              color={c}
            />
          </div>
        </div>
        <>
          {/* 1. Knowledge Base aziendale sub-section */}
          <div
            style={{
              borderTop: `1px solid ${t.border}`,
              paddingTop: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              <span style={{ ...lbl, marginBottom: 0, flexShrink: 0 }}>
                <BookOpen size={12} /> KNOWLEDGE BASE AZIENDALE{" "}
                <Tooltip
                  text="Base di conoscenza precaricata del tuo tenant (sito web, documenti aziendali). Il sistema cerca informazioni rilevanti per arricchire il contenuto. Puoi specificare una query di ricerca personalizzata."
                  color={c}
                />
              </span>
              {kbSearch.enabled && (
                <input
                  type="text"
                  value={kbSearch.query}
                  onChange={(e) =>
                    set("kbSearch", { ...kbSearch, query: e.target.value })
                  }
                  placeholder="BILANCIO DI SOSTENIBILITÀ, COACHING ESG"
                  style={{ ...inp, flex: 1 }}
                />
              )}
              <div
                onClick={() =>
                  set("kbSearch", { ...kbSearch, enabled: !kbSearch.enabled })
                }
                style={{
                  width: "36px",
                  height: "20px",
                  borderRadius: "10px",
                  background: kbSearch.enabled ? c : w(0.08),
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: "2px",
                    left: kbSearch.enabled ? "18px" : "2px",
                    transition: "left 0.2s",
                  }}
                />
              </div>
            </div>
            <HelperLine>
              Cerca tra le fonti aziendali pre-indicizzate. Se il campo qui
              sopra viene lasciato vuoto, viene ricercato il contenuto di
              "DI COSA VUOI PARLARE"
            </HelperLine>
          </div>
          {/* 2. Knowledge Base Utente sub-section */}
          <div
            style={{
              borderTop: `1px solid ${t.border}`,
              paddingTop: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              <span style={{ ...lbl, marginBottom: 0, flexShrink: 0 }}>
                <Upload size={12} /> DOCUMENTI CARICATI{" "}
                <Tooltip
                  text="Carica i tuoi documenti (PDF, Word, PPT, Excel, TXT, MD) come fonte aggiuntiva. I file vengono analizzati e utilizzati per generare contenuti basati sui tuoi materiali specifici."
                  color={c}
                />
              </span>
              {userKbEnabled && uploadBundle.files.length > 0 && (
                <input
                  type="text"
                  value={userKbQuery}
                  onChange={(e) => set("userKbQuery", e.target.value)}
                  placeholder="Lascia vuoto per usare l'Argomento"
                  style={{ ...inp, flex: 1 }}
                />
              )}
              <div
                onClick={() => set("userKbEnabled", !userKbEnabled)}
                style={{
                  width: "36px",
                  height: "20px",
                  borderRadius: "10px",
                  background: userKbEnabled ? c : w(0.08),
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: "2px",
                    left: userKbEnabled ? "18px" : "2px",
                    transition: "left 0.2s",
                  }}
                />
              </div>
            </div>
            <HelperLine>
              Cerca tra i documenti caricati in questa sessione (PDF, DOCX,
              PPTX, XLSX, TXT, MD). Disponibile solo hai caricato almeno un
              documento.
            </HelperLine>
            {userKbEnabled && (
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  position: "relative",
                }}
              >
                {/* Loading overlay */}
                {uploadBundle.isLoading && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 10,
                      background: t.surface + "e6",
                      borderRadius: "10px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "28px",
                        height: "28px",
                        border: `3px solid ${c}30`,
                        borderTop: `3px solid ${c}`,
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                    <span
                      style={{ fontSize: "12px", fontWeight: 600, color: c }}
                    >
                      Caricamento in corso...
                    </span>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}
                {/* Empty-state hint — appare quando ON ma 0 file */}
                {uploadBundle.files.length === 0 && !uploadBundle.isLoading && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: t.textFaint,
                      fontStyle: "italic",
                      marginBottom: "4px",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    Carica documenti per attivare questa fonte.
                    Coaching_ESG_BilancioSostenibilità.pdf ad esempio.
                  </div>
                )}
                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() =>
                    !uploadBundle.isLoading && fileInputRef.current?.click()
                  }
                  style={{
                    border: `2px dashed ${dragOver ? c : t.inputBorder}`,
                    borderRadius: "10px",
                    padding: "20px",
                    textAlign: "center",
                    cursor: uploadBundle.isLoading ? "not-allowed" : "pointer",
                    background: dragOver ? `${c}08` : t.inputBg,
                    transition: "all 0.2s",
                    opacity: uploadBundle.isLoading ? 0.4 : 1,
                  }}
                >
                  <Upload
                    size={20}
                    style={{
                      color: dragOver ? c : t.textFaint,
                      marginBottom: "6px",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: dragOver ? c : t.text,
                    }}
                  >
                    Trascina un file qui
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: t.textFaint,
                      marginTop: "4px",
                    }}
                  >
                    oppure clicca per selezionare — PDF, DOCX, PPTX, XLSX, TXT,
                    MD (max 50 MB)
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  accept=".pdf,.docx,.pptx,.xlsx,.txt,.md"
                />
                {uploadBundle.error && (
                  <div style={{ color: "#EF4444", fontSize: "12px" }}>
                    {uploadBundle.error}
                  </div>
                )}
                {uploadBundle.files.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    {uploadBundle.files.map((f) => (
                      <div
                        key={f.filename}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "6px 10px",
                          borderRadius: "8px",
                          background: t.inputBg,
                          border: `1px solid ${t.inputBorder}`,
                          fontSize: "12px",
                          color: t.text,
                        }}
                      >
                        <FileText
                          size={14}
                          style={{ color: c, flexShrink: 0 }}
                        />
                        <span
                          style={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {f.filename}
                        </span>
                        {f.chunks != null && (
                          <span
                            style={{
                              color: t.textFaint,
                              fontSize: "10px",
                              flexShrink: 0,
                            }}
                          >
                            {f.chunks} chunks
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFile(f.filename);
                          }}
                          disabled={uploadBundle.isLoading}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: t.textFaint,
                            padding: "2px",
                            flexShrink: 0,
                            opacity: uploadBundle.isLoading ? 0.4 : 1,
                          }}
                          title="Rimuovi file"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* 3. URL di Riferimento sub-section */}
          <div
            style={{
              borderTop: `1px solid ${t.border}`,
              paddingTop: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              <span style={{ ...lbl, marginBottom: 0, flexShrink: 0 }}>
                <Paperclip size={12} /> URL DI RIFERIMENTO{" "}
                <Tooltip
                  text="Inserisci fino a 3 URL di pagine web da usare come riferimento diretto. Il sistema analizzerà il contenuto di queste pagine per ispirare o basare la generazione."
                  color={c}
                />
              </span>
              <div
                onClick={() => set("refsEnabled", !refsEnabled)}
                style={{
                  width: "36px",
                  height: "20px",
                  borderRadius: "10px",
                  background: refsEnabled ? c : w(0.08),
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: "2px",
                    left: refsEnabled ? "18px" : "2px",
                    transition: "left 0.2s",
                  }}
                />
              </div>
            </div>
            <HelperLine>
              URL specifici da scaricare come evidence. I contenuti vengono
              recuperati tramite browser headless e passati al Writer.
            </HelperLine>
            {refsEnabled && (
              <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                {referenceUrls.map((url, i) => (
                  <input
                    key={i}
                    type="url"
                    value={url}
                    onChange={(e) =>
                      dispatch({
                        type: "SET_REFERENCE_URL_AND_INVALIDATE",
                        index: i,
                        value: e.target.value,
                      })
                    }
                    onBlur={(e) => {
                      const n = normalizeUrl(e.target.value);
                      if (n !== e.target.value)
                        dispatch({
                          type: "SET_REFERENCE_URL_AND_INVALIDATE",
                          index: i,
                          value: n,
                        });
                    }}
                    placeholder="https://esempio.com/articolo"
                    style={{ ...inp, flex: 1 }}
                  />
                ))}
              </div>
            )}
          </div>
          {/* 4. Ricerca Web sub-section */}
          <div
            style={{ borderTop: `1px solid ${t.border}`, paddingTop: "16px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              <span style={{ ...lbl, marginBottom: 0, flexShrink: 0 }}>
                <Search size={12} /> RICERCA WEB{" "}
                <Tooltip
                  text="Abilita la ricerca web per trovare informazioni aggiornate sull'argomento. Utile per dati recenti, trend e informazioni non presenti nella KB. Puoi personalizzare la query di ricerca."
                  color={c}
                />
              </span>
              {webSearch.enabled && (
                <input
                  type="text"
                  value={webSearch.query}
                  onChange={(e) =>
                    set("webSearch", { ...webSearch, query: e.target.value })
                  }
                  placeholder="Aggiornamenti normativi 2025 bilancio di sostenibilita"
                  style={{ ...inp, flex: 1 }}
                />
              )}
              <div
                onClick={() =>
                  set("webSearch", {
                    ...webSearch,
                    enabled: !webSearch.enabled,
                  })
                }
                style={{
                  width: "36px",
                  height: "20px",
                  borderRadius: "10px",
                  background: webSearch.enabled ? c : w(0.08),
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: "2px",
                    left: webSearch.enabled ? "18px" : "2px",
                    transition: "left 0.2s",
                  }}
                />
              </div>
            </div>
            <HelperLine>
              Cerca contenuti recenti sul web (statistiche, articoli di settore,
              dati pubblici).
            </HelperLine>
          </div>
        </>
      </div>

    </div>
  );
};
export default Step1Setup;
